/* eslint-disable react-hooks/exhaustive-deps */
import ImageModal from "components/ImageModal";
import imageVector from "images/imageVector.png";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import AddDailyReport from "pages/InstituteOfCharge/modal/AddDailyReport";
import AppealRequestForm from "pages/InstituteOfCharge/modal/AppealRequestForm";
import PreJugmentForm from "pages/InstituteOfCharge/modal/PreJugmentForm";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CaseCreation,
  DailyReport,
  FILE_URL,
  FinalVerdict,
  PreJudgement,
  Suspect,
  Witness,
} from "types";
import { constructFileUrl } from "utils/helper";
import AddFinalVerdict from "./AddFinalVerdict";
import { fetchRemands } from "services/courtCaseService";

const OpeningCaseFileStatus: { [key: string]: string } = {
  Pending: "Pending",
  forwarded_to_registrar: "Forwarded to Registrar",
  forwarded_to_coordinator: "Forwarded to Coordinator",
  assigned_to_prosecutor: "Assigned to Prosecutor",
  in_progress: "In Progress",
  scheduled: "Scheduled",
  decision_provided: "Decision Provided",
  pre_judgement_provided: "Pre Judgement Provided",
};

type Props = {
  dailyReports?: DailyReport[];
  finalVerdicts?: FinalVerdict[];
  setSelectedDailyReport: React.Dispatch<
    React.SetStateAction<DailyReport | undefined>
  >;
  selectedDailyReport?: DailyReport;
  isProsecutor?: boolean;
  isRegistrar?: boolean;
  isCoordinator?: boolean;
  caseData: CaseCreation;
  fetchData: () => void;
};

const Report: React.FC<Props> = ({
  setSelectedDailyReport,
  selectedDailyReport,
  isProsecutor = false,
  isRegistrar = false,
  isCoordinator = false,
  caseData,
  fetchData,
}) => {
  const { t } = useTranslation();
  const [selectedEntity, setSelectedEntity] = useState<Suspect>();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState({
    suspect: false,
    witness: false,
  });
  const [isAddPreJudgment, setIsAddPreJudgment] = useState(false);
  const [isAddDailyReportOpen, setIsAddDailyReportOpen] = useState(false);
  const [isAddFinalVerdictOpen, setIsAddFinalVerdictOpen] = useState(false);
  const [finalVerdictAction, setFinalVerdictAction] = useState<"Add" | "Edit">(
    "Add",
  );
  // A remand lets the prosecutor update the final verdict after it was given.
  const [hasRemand, setHasRemand] = useState(false);
  // Once the final decision file is on file, no more daily reports are added.
  const hasFinalDecision = caseData?.documents?.some(
    (d) => d?.document_type === "final_court_decision",
  );
  // Verdicts already updated this session — hide their Update button.
  const [updatedVerdictIds, setUpdatedVerdictIds] = useState<Set<number>>(
    new Set(),
  );
  const [activeTab, setActiveTab] = useState<
    "hearing" | "dailyReport" | "finalVerdict"
  >("hearing");
  const [totalCases, setTotalCases] = useState(0);
  const [totalGuilt, setTotalGuilt] = useState(0);
  const [openVerdictIndex, setOpenVerdictIndex] = useState<number | null>(null);
  const [openPreJudgementIndexes, setOpenPreJudgementIndexes] = useState<
    number[]
  >([]);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [selectedData, setSelectedData] = useState<FinalVerdict>();

  const formatOpeningCaseStatus = (status?: string) => {
    if (!status) return t("na");

    return t(`status_${status.toLowerCase()}`, {
      defaultValue: OpeningCaseFileStatus[status] || status,
    });
  };

  const formatDecisionValue = (value?: string | null) => {
    if (!value) return t("na");

    const fallback = value
      ?.replace(/_/g, " ")
      .replace(/\b\w/g, (c: any) => c.toUpperCase());

    return t(`decision_${value.toLowerCase()}`, { defaultValue: fallback });
  };

  useEffect(() => {
    if (Array.isArray(caseData?.counts)) {
      const countsArr = caseData.counts as any[];
      const total1 = countsArr.reduce(
        (acc: number, count: any) =>
          acc + (count?.defendants_info?.length || 0),
        0,
      );
      const total1_alternate = countsArr
        ?.filter((c) => c?.has_alternate)
        .reduce(
          (acc: number, count: any) =>
            acc + (count?.defendants_info?.length || 0),
          0,
        );

      setTotalCases(total1 + total1_alternate);

      const preArr = caseData.PreJudgementEntry as PreJudgement[];
      const total2 = preArr?.filter((p) => p?.decision !== "not_gulity");
      setTotalGuilt(total2.length);
    } else {
      setTotalCases(0);
    }
  }, [caseData?.counts]);

  // Does this case have a remand? If so, the prosecutor may update the verdict.
  useEffect(() => {
    if (!caseData?.id) return;
    fetchRemands({ case_id: String(caseData.id) })
      .then((data) => setHasRemand(Array.isArray(data) && data.length > 0))
      .catch(() => setHasRemand(false));
  }, [caseData?.id]);

  const buildView = (data: Suspect | Witness, type: "suspect" | "witness") => {
    return (
      <div className="border border-gray-200 rounded-lg p-4 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={(() => {
                const doc = data?.documents?.find(
                  (doc) => doc.document_type === "suspect_picture",
                );
                return doc
                  ? constructFileUrl(doc, FILE_URL.SUSPECT)
                  : imageVector;
              })()}
              alt={t("report_user_image_alt")}
              className="w-12 h-12 rounded-full cursor-pointer"
              onClick={() => {
                setIsImageModalOpen(true);
                setSelectedEntity(data);
              }}
            />
            <div>
              <h4 className="font-medium text-gray-900">
                {data?.first_name} {data?.middle_name} {data?.last_name}
              </h4>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                {data?.age && (
                  <span>
                    {data.age} {t("years")}
                  </span>
                )}
                {data?.phone_number && (
                  <>
                    <span>•</span>
                    <span>{data.phone_number}</span>
                  </>
                )}
                {data?.nationality && (
                  <>
                    <span>•</span>
                    <span>{data.nationality}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setIsDetailModalOpen({
                suspect: type === "suspect",
                witness: type === "witness",
              });
              setSelectedEntity(data);
            }}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t("view_details_button")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("reports")}
          </h3>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("hearing")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "hearing"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {t("institute_of_charge_detail_tab_hearing")}
          </button>
          <button
            onClick={() => setActiveTab("dailyReport")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "dailyReport"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {t("daily_report")}
          </button>
          <button
            onClick={() => setActiveTab("finalVerdict")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "finalVerdict"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {t("final_verdict")}
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-6">
        {activeTab === "hearing" &&
          caseData?.PreJudgementEntry &&
          caseData?.case_defendant_links &&
          caseData?.PreJudgementEntry?.length < (totalCases || 0) &&
          caseData?.opening_case_files &&
          // caseData?.opening_case_files[0]?.status ===
          //   "assigned_to_prosecutor" &&
          isProsecutor && (
            <button
              onClick={() => setIsAddPreJudgment(true)}
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              + {t("report_add_hearing_report")}
            </button>
          )}

        {activeTab === "dailyReport" &&
          !hasFinalDecision &&
          caseData?.PreJudgementEntry &&
          caseData?.PreJudgementEntry?.length > 0 &&
          caseData?.opening_case_files &&
          // Status check relaxed (consistent with the hearing report button)
          // so the prosecutor can add daily reports on the court hearing.
          // caseData?.opening_case_files[0]?.status ===
          //   "assigned_to_prosecutor" &&
          isProsecutor &&
          !caseData?.is_article_of_cahrge_changed &&
          caseData?.status !== "case_terminated_by_court" && (
            <button
              onClick={() => setIsAddDailyReportOpen(true)}
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              + {t("report_add_daily_report")}
            </button>
          )}

        {activeTab === "finalVerdict" &&
          caseData?.counts &&
          caseData?.opening_case_files &&
          // Status check relaxed (consistent with the hearing report button)
          // so the prosecutor can add the final verdict on the court hearing.
          // caseData?.opening_case_files[0]?.status ===
          //   "assigned_to_prosecutor" &&
          caseData?.FinalVerdict &&
          Array.isArray(caseData?.counts) &&
          caseData?.FinalVerdict?.length < totalGuilt &&
          isProsecutor &&
          !caseData?.is_article_of_cahrge_changed &&
          caseData?.status !== "case_terminated_by_court" && (
            <button
              onClick={() => {
                setFinalVerdictAction("Add");
                setIsAddFinalVerdictOpen(true);
              }}
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              + {t("report_add_final_verdict")}
            </button>
          )}

      </div>

      {/* Content */}
      <div className="max-h-[600px] overflow-y-auto pr-2">
        {activeTab === "hearing" &&
          Array.isArray(caseData?.PreJudgementEntry) &&
          caseData.PreJudgementEntry.length > 0 &&
          (() => {
            const preJudgementArr =
              caseData.PreJudgementEntry as PreJudgement[];
            const grouped = preJudgementArr.reduce(
              (
                acc: { [countNum: string]: PreJudgement[] },
                entry: PreJudgement,
              ) => {
                const countNum =
                  entry?.count_defendant_info?.count_number ?? "Unknown";
                if (!acc[countNum]) acc[countNum] = [];
                acc[countNum].push(entry);
                return acc;
              },
              {},
            );

            return Object.keys(grouped).map((countNum) => {
              const entries = grouped[countNum];

              return (
                <div
                  key={countNum}
                  className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <span className="text-red-600">#</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      {t("report_count_number", { number: countNum })}
                    </h4>
                  </div>

                  {(entries as PreJudgement[]).map((entry, idx) => {
                    const open = openPreJudgementIndexes.includes(
                      Number(`${countNum}${idx}`),
                    );
                    return (
                      <div key={idx} className="mb-4 last:mb-0">
                        <button
                          onClick={() => {
                            setOpenPreJudgementIndexes((prev) =>
                              open
                                ? prev.filter(
                                    (i) => i !== Number(`${countNum}${idx}`),
                                  )
                                : [...prev, Number(`${countNum}${idx}`)],
                            );
                          }}
                          className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {t("report_defendant_number", {
                                number: entry?.defendant_info?.defendant_number,
                              })}
                              :{" "}
                              {
                                entry?.defendant_info?.defendant_info
                                  ?.first_name
                              }{" "}
                              {
                                entry?.defendant_info?.defendant_info
                                  ?.middle_name
                              }{" "}
                              {entry?.defendant_info?.defendant_info?.last_name}
                            </span>
                            {entry?.has_alternate && (
                              <span className="ml-3 px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800">
                                {t("alternate")}
                              </span>
                            )}
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-500 transition-transform ${
                              open ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>

                        {open && (
                          <div className="mt-3 p-4 bg-white rounded-lg border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div>
                                <span className="text-xs font-medium text-gray-500">
                                  {t("defendant")}
                                </span>
                                <p className="text-sm font-medium text-gray-900">
                                  {
                                    entry?.defendant_info?.defendant_info
                                      ?.first_name
                                  }{" "}
                                  {
                                    entry?.defendant_info?.defendant_info
                                      ?.middle_name
                                  }{" "}
                                  {
                                    entry?.defendant_info?.defendant_info
                                      ?.last_name
                                  }
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500">
                                  {t("decision")}
                                </span>
                                <p className="text-sm font-medium text-gray-900">
                                  {formatDecisionValue(entry?.decision)}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500">
                                  {t("decision_date")}
                                </span>
                                <p className="text-sm font-medium text-gray-900">
                                  {entry?.decision_date
                                    ? new Date(
                                        entry.decision_date,
                                      ).toDateString()
                                    : t("na")}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <span className="text-xs font-medium text-gray-500">
                                  {t("summary")}
                                </span>
                                <p className="text-sm text-gray-600 mt-1">
                                  {entry?.summary || t("na")}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500">
                                  {t("remarks")}
                                </span>
                                <p className="text-sm text-gray-600 mt-1">
                                  {entry?.remarks || t("na")}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })()}

        {activeTab === "dailyReport" &&
          caseData?.DailyReport &&
          caseData.DailyReport.slice()
            .sort((a, b) => {
              const dateA = a.next_trial_appointment
                ? new Date(a.next_trial_appointment).getTime()
                : 0;
              const dateB = b.next_trial_appointment
                ? new Date(b.next_trial_appointment).getTime()
                : 0;
              return dateB - dateA;
            })
            .map((report, index) => (
              <div
                key={index}
                className={`mb-4 cursor-pointer ${
                  selectedDailyReport?.id === report.id
                    ? "ring-2 ring-red-500"
                    : ""
                }`}
                onClick={() => setSelectedDailyReport(report)}
              >
                <div className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-blue-600">📅</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {report?.next_trial_appointment &&
                            new Date(
                              report?.next_trial_appointment,
                            )?.toDateString()}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {report?.appointment_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {report?.status &&
                          formatOpeningCaseStatus(report?.status)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-medium text-gray-500">
                        {t("appointment_reason")}
                      </span>
                      <p className="text-sm text-gray-600">
                        {report?.appointment_reason}
                      </p>
                    </div>

                    <div>
                      <span className="text-xs font-medium text-gray-500">
                        {t("daily_report_summary")}
                      </span>
                      <p className="text-sm text-gray-600">
                        {report?.dailyreport_summary || t("na")}
                      </p>
                    </div>

                    <div>
                      <span className="text-xs font-medium text-gray-500">
                        {t("court_order")}
                      </span>
                      <p className="text-sm text-gray-600">
                        {report?.court_order_for_next_appointment || t("na")}
                      </p>
                    </div>

                    {report?.defendants_info?.map((defendant, index) => (
                      <div key={index}>{buildView(defendant, "suspect")}</div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

        {activeTab === "finalVerdict" &&
          caseData?.FinalVerdict &&
          caseData.FinalVerdict.length > 0 && (
            <div className="space-y-4">
              {caseData?.final_court_decision_info?.file_name && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <span className="text-red-600">⚖️</span>
                    </div>
                    <h4 className="text-lg font-semibold text-red-900">
                      {t("report_final_verdict_document")}
                    </h4>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-600">📄</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {caseData?.final_court_decision_info?.file_name ||
                            t("document")}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        window.open(
                          caseData?.final_court_decision_info?.file_url,
                          "_blank",
                        )
                      }
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t("report_view_document")}
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-t from-[#00582F] to-[#12B76A] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-white">⚖️</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">
                      {t("report_final_verdict_details")}
                    </h4>
                    <div className="text-white/80 text-sm mt-1">
                      <p>
                        {t("report_prosecutor_registration_number")}:{" "}
                        <span className="font-semibold">
                          {caseData?.prosecutor_registration_number}
                        </span>
                      </p>
                      <p>
                        {t("report_court_registration_number")}:{" "}
                        <span className="font-semibold">
                          {caseData?.opening_case_files?.[0]
                            ?.court_registration_number || t("na")}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {caseData.FinalVerdict.map((report, index) => {
                    const open = openVerdictIndex === index;
                    const isAlternate = Boolean(report?.has_alternate);
                    return (
                      <div
                        key={index}
                        className="bg-white/10 rounded-lg border border-white/20 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            {report?.case_defendant_link_info
                              ?.defendant_info && (
                              <div className="flex items-center gap-3">
                                <img
                                  src={(() => {
                                    const doc =
                                      report?.case_defendant_link_info?.defendant_info?.documents?.find(
                                        (doc) =>
                                          doc.document_type ===
                                          "suspect_picture",
                                      );
                                    return doc
                                      ? constructFileUrl(doc, FILE_URL.SUSPECT)
                                      : imageVector;
                                  })()}
                                  alt={t("defendant")}
                                  className="w-10 h-10 rounded-full"
                                />
                                <div>
                                  <h5 className="font-medium text-white">
                                    {
                                      report?.case_defendant_link_info
                                        ?.defendant_info?.first_name
                                    }{" "}
                                    {
                                      report?.case_defendant_link_info
                                        ?.defendant_info?.middle_name
                                    }{" "}
                                    {
                                      report?.case_defendant_link_info
                                        ?.defendant_info?.last_name
                                    }
                                  </h5>
                                  <div className="text-sm text-white/80">
                                    {report?.court_decision_summary}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isAlternate && (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-500 text-white">
                                {t("alternate")}
                              </span>
                            )}
                            <button
                              onClick={() =>
                                setOpenVerdictIndex(open ? null : index)
                              }
                              className="text-white hover:text-white/80"
                            >
                              <svg
                                className={`w-5 h-5 transition-transform ${
                                  open ? "rotate-180" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {open && (
                          <div className="mt-4 pt-4 border-t border-white/20">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <span className="text-xs font-medium text-white/80">
                                  {t("report_court_decision")}
                                </span>
                                <p className="text-sm font-medium text-white">
                                  {report?.court_decision
                                    ? String(report.court_decision)
                                    : t("na")}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-white/80">
                                  {t("report_decision_amount")}
                                </span>
                                <p className="text-sm font-medium text-white">
                                  {report?.court_decision_amount || t("na")}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-white/80">
                                  {t("report_decision_type")}
                                </span>
                                <p className="text-sm font-medium text-white">
                                  {report?.court_decision_type
                                    ? String(report.court_decision_type)
                                    : t("na")}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              {report?.count_defendant_info?.has_alternate && (
                                <>
                                  <div>
                                    <span className="text-xs font-medium text-white/80">
                                      {t("alternate_crime_type")}
                                    </span>
                                    <p className="text-sm text-white">
                                      {report?.count_defendant_info
                                        ?.alternate_crime_type || t("na")}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-xs font-medium text-white/80">
                                      {t("alternate_crime_level")}
                                    </span>
                                    <p className="text-sm text-white">
                                      {report?.count_defendant_info
                                        ?.alternate_crime_level || t("na")}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="mt-4">
                              <span className="text-xs font-medium text-white/80">
                                {t("summary")}
                              </span>
                              <p className="text-sm text-white mt-1">
                                {report?.court_decision_summary || t("na")}
                              </p>
                            </div>

                            <div className="mt-4">
                              <span className="text-xs font-medium text-white/80">
                                {t("prosecutor_feedback")}
                              </span>
                              <p className="text-sm text-white mt-1">
                                {report?.prosecutor_feedback_on_decision ||
                                  t("na")}
                              </p>
                            </div>

                            {isProsecutor && (
                              <div className="mt-4 flex justify-end gap-3">
                                {hasRemand &&
                                  !updatedVerdictIds.has(report.id) &&
                                  !caseData?.is_article_of_cahrge_changed &&
                                  caseData?.status !==
                                    "case_terminated_by_court" && (
                                    <button
                                      onClick={() => {
                                        setSelectedData(report);
                                        setFinalVerdictAction("Edit");
                                        setIsAddFinalVerdictOpen(true);
                                      }}
                                      className="px-4 py-2 text-sm bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors"
                                    >
                                      ✏️{" "}
                                      {t("report_update_final_verdict", {
                                        defaultValue: "Update Decision",
                                      })}
                                    </button>
                                  )}
                                <button
                                  onClick={() => {
                                    setSelectedData(report);
                                    setOpenAppealForm(true);
                                  }}
                                  className="px-4 py-2 text-sm bg-white text-green-700 font-medium rounded-lg hover:bg-white/90 transition-colors"
                                >
                                  {t("request_appeal")}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Empty State */}
      {caseData?.PreJudgementEntry &&
        caseData.PreJudgementEntry.length === 0 &&
        activeTab === "hearing" && (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
            {t("report_no_hearing_reports_found")}
          </div>
        )}

      {caseData?.DailyReport?.length === 0 && activeTab === "dailyReport" && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
          {t("report_no_daily_reports_found")}
        </div>
      )}

      {(!caseData?.FinalVerdict || caseData.FinalVerdict.length === 0) &&
        activeTab === "finalVerdict" && (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
            {t("report_no_final_verdicts_found")}
          </div>
        )}

      {/* Modals */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={(() => {
            const doc = selectedEntity?.documents?.find(
              (doc) => doc.document_type === "suspect_picture",
            );
            return doc ? constructFileUrl(doc, FILE_URL.SUSPECT) : imageVector;
          })()}
          label={`${selectedEntity?.first_name} ${selectedEntity?.middle_name} ${selectedEntity?.last_name}`}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {isDetailModalOpen?.suspect && (
        <SuspectDetailInfo
          suspect={selectedEntity as Suspect}
          isOpen={isDetailModalOpen?.suspect}
          isDefendant={true}
          onClose={() =>
            setIsDetailModalOpen({ suspect: false, witness: false })
          }
        />
      )}

      {isAddPreJudgment && caseData && (
        <PreJugmentForm
          caseData={caseData}
          onClose={(reload) => {
            setIsAddPreJudgment(false);
            if (reload) fetchData();
          }}
        />
      )}

      {isAddDailyReportOpen && caseData && (
        <AddDailyReport
          caseData={caseData}
          action="Add"
          onClose={(reload) => {
            setIsAddDailyReportOpen(false);
            if (reload) fetchData();
          }}
        />
      )}

      {isAddFinalVerdictOpen && caseData && (
        <AddFinalVerdict
          caseData={caseData}
          action={finalVerdictAction}
          finalVerdict={
            finalVerdictAction === "Edit" ? selectedData : undefined
          }
          onClose={(reload) => {
            if (
              reload &&
              finalVerdictAction === "Edit" &&
              selectedData?.id != null
            ) {
              setUpdatedVerdictIds((prev) => {
                const next = new Set(prev);
                next.add(selectedData.id);
                return next;
              });
            }
            setIsAddFinalVerdictOpen(false);
            setFinalVerdictAction("Add");
            setSelectedData(undefined);
            if (reload) fetchData();
          }}
        />
      )}

      {openAppealForm && (
        <AppealRequestForm
          caseData={caseData}
          finalVerdict={selectedData}
          appealType="FINAL_VERDICT"
          onClose={() => setOpenAppealForm(false)}
        />
      )}
    </div>
  );
};

export default Report;
